import { useEffect, useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import api, { errMsg } from "@/lib/api";
import AdminShell from "@/components/AdminShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { inr } from "@/lib/constants";
import { Copy } from "lucide-react";

/* ===== Super Admin Overview ===== */
export function SuperAdminOverview() {
    const [dash, setDash] = useState(null);
    const [settings, setSettings] = useState(null);
    const nav = useNavigate();

    useEffect(() => {
        Promise.all([api.get("/admin/dashboard"), api.get("/super-admin/system-settings")])
            .then(([d, s]) => { setDash(d.data); setSettings(s.data); })
            .catch((e) => toast.error(errMsg(e, "Failed to load")));
    }, []);

    return (
        <AdminShell>
            <div className="mb-6" data-testid="super-overview-header">
                <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin</div>
                <h1 className="text-2xl font-bold text-[#123B5D]">Platform overview</h1>
                <p className="text-[#17212B]/60 mt-1 text-sm">All admin metrics plus platform-wide controls.</p>
            </div>
            {!dash ? <div className="text-[#17212B]/60">Loading…</div> : (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Card className="border-[#123B5D]/10"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Doctors</CardTitle></CardHeader>
                        <CardContent><div className="text-2xl font-bold text-[#123B5D]">{dash.metrics.total_doctors}</div></CardContent></Card>
                    <Card className="border-[#123B5D]/10"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Published</CardTitle></CardHeader>
                        <CardContent><div className="text-2xl font-bold text-[#123B5D]">{dash.metrics.published_websites}</div></CardContent></Card>
                    <Card className="border-[#123B5D]/10"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Revenue (month)</CardTitle></CardHeader>
                        <CardContent><div className="text-2xl font-bold text-[#123B5D]">{inr(dash.metrics.revenue_monthly_inr)}</div></CardContent></Card>
                    <Card className="border-[#123B5D]/10"><CardHeader className="pb-2"><CardTitle className="text-xs uppercase tracking-widest text-[#123B5D]/60">Revenue (total)</CardTitle></CardHeader>
                        <CardContent><div className="text-2xl font-bold text-[#123B5D]">{inr(dash.metrics.revenue_total_inr)}</div></CardContent></Card>
                </div>
            )}
            <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                    { label: "Packages", to: "/super-admin/packages", desc: "Edit prices, AMC, and toggle active" },
                    { label: "Features", to: "/super-admin/features", desc: "Feature matrix + global feature flags" },
                    { label: "Templates", to: "/super-admin/templates", desc: "Toggle any of the 44 templates on/off globally" },
                    { label: "Admin users", to: "/super-admin/admins", desc: "Create/disable admin & super-admin accounts" },
                    { label: "Audit logs", to: "/super-admin/audit", desc: "Every sensitive action recorded" },
                    { label: "Integrations", to: "/super-admin/integrations", desc: "Meta, Google, Cashfree, AI — credentials + live/demo toggle" },
                    { label: "System settings", to: "/super-admin/settings", desc: "Payment gateway + AI config (read-only)" },
                    { label: "Demo data cleanup", to: "/super-admin/maintenance", desc: "Unpublish or delete test / QA / demo accounts before launch" },
                    { label: "Site content · CMS", to: "/super-admin/site-content", desc: "Edit landing hero, testimonials, FAQ, footer, contact info, legal copy" },
                    { label: "Communications", to: "/super-admin/communications", desc: "Email · SMS · WhatsApp templates + lifecycle audit + PDF report" },
                ].map((c) => (
                    <button key={c.to} onClick={() => nav(c.to)} className="rounded-xl border border-[#123B5D]/10 bg-white p-4 text-left hover:border-[#0F766E] transition" data-testid={`super-card-${c.to.split('/').pop()}`}>
                        <div className="text-base font-bold text-[#123B5D]">{c.label}</div>
                        <div className="text-xs text-[#17212B]/60 mt-1">{c.desc}</div>
                    </button>
                ))}
            </div>
        </AdminShell>
    );
}

/* ===== Packages ===== */
export function SuperAdminPackages() {
    const [pkgs, setPkgs] = useState([]);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState({});
    useEffect(() => { api.get("/super-admin/packages").then((r) => setPkgs(r.data)); }, []);

    function change(code, field, value) {
        setEdits((e) => ({ ...e, [code]: { ...(e[code] || {}), [field]: value } }));
    }
    async function save(code) {
        const patch = edits[code];
        if (!patch) return;
        setSaving((s) => ({ ...s, [code]: true }));
        try {
            const r = await api.put(`/super-admin/packages/${code}`, patch);
            toast.success(`${r.data.name} saved`);
            setPkgs((p) => p.map((x) => x.code === code ? r.data : x));
            setEdits((e) => { const c = { ...e }; delete c[code]; return c; });
        } catch (e) {
            toast.error(errMsg(e, "Save failed"));
        } finally {
            setSaving((s) => ({ ...s, [code]: false }));
        }
    }
    return (
        <AdminShell>
            <div className="mb-6"><h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-packages-header">Packages</h1><p className="text-[#17212B]/60 text-sm">Edits reflect on /pricing immediately.</p></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {pkgs.map((p) => {
                    const patch = edits[p.code] || {};
                    const val = (k) => patch[k] !== undefined ? patch[k] : p[k];
                    return (
                        <Card key={p.code} className="border-[#123B5D]/10" data-testid={`pkg-card-${p.code}`}>
                            <CardHeader><CardTitle className="text-[#123B5D]">{p.name}</CardTitle></CardHeader>
                            <CardContent className="space-y-3">
                                <div><div className="text-xs text-[#17212B]/60">Price (INR)</div>
                                    <Input type="number" value={val("price_inr")} onChange={(e) => change(p.code, "price_inr", parseInt(e.target.value || "0", 10))} data-testid={`pkg-${p.code}-price`} />
                                </div>
                                <div><div className="text-xs text-[#17212B]/60">AMC (INR)</div>
                                    <Input type="number" value={val("amc_inr")} onChange={(e) => change(p.code, "amc_inr", parseInt(e.target.value || "0", 10))} data-testid={`pkg-${p.code}-amc`} />
                                </div>
                                <div className="flex items-center gap-3">
                                    <Switch checked={val("is_active")} onCheckedChange={(v) => change(p.code, "is_active", v)} data-testid={`pkg-${p.code}-active`} />
                                    <span className="text-sm text-[#17212B]/70">Active on /pricing</span>
                                </div>
                                <Button size="sm" onClick={() => save(p.code)} disabled={!edits[p.code] || saving[p.code]} data-testid={`pkg-${p.code}-save`}>
                                    {saving[p.code] ? "Saving…" : "Save"}
                                </Button>
                            </CardContent>
                        </Card>
                    );
                })}
            </div>
        </AdminShell>
    );
}

/* ===== Features + flags ===== */
export function SuperAdminFeatures() {
    const [features, setFeatures] = useState([]);
    const [busy, setBusy] = useState({});
    const [q, setQ] = useState("");

    useEffect(() => { api.get("/super-admin/features").then((r) => setFeatures(r.data)); }, []);

    async function toggleFlag(code, enabled) {
        setBusy((b) => ({ ...b, [code]: true }));
        try {
            await api.put(`/super-admin/feature-flags/${code}`, { enabled });
            setFeatures((fs) => fs.map((f) => f.feature_code === code ? { ...f, flag_enabled: enabled } : f));
            toast.success(`${code} ${enabled ? "enabled" : "disabled"}`);
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally {
            setBusy((b) => ({ ...b, [code]: false }));
        }
    }

    const filtered = features.filter((f) => {
        if (!q) return true;
        const s = q.toLowerCase();
        return f.feature_code.toLowerCase().includes(s) || f.name.toLowerCase().includes(s) || f.module_name.toLowerCase().includes(s);
    });

    return (
        <AdminShell>
            <div className="mb-6 flex items-baseline justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-features-header">Features</h1>
                    <p className="text-[#17212B]/60 text-sm">Toggle any feature OFF globally — locked doctors see "Temporarily unavailable".</p>
                </div>
                <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" className="max-w-xs" data-testid="features-search" />
            </div>
            <Card className="border-[#123B5D]/10">
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                            <tr><th className="text-left p-3">Code</th><th className="text-left p-3">Feature</th><th className="text-left p-3">Module</th><th className="text-left p-3">Min tier</th><th className="text-left p-3">Status</th><th className="text-right p-3">Global flag</th></tr>
                        </thead>
                        <tbody>
                            {filtered.map((f) => (
                                <tr key={f.feature_code} className="border-b border-[#123B5D]/5" data-testid={`feature-row-${f.feature_code}`}>
                                    <td className="p-3 font-mono text-xs">{f.feature_code}</td>
                                    <td className="p-3">{f.name}</td>
                                    <td className="p-3 text-xs">{f.module_code} · {f.module_name}</td>
                                    <td className="p-3 text-xs">{f.min_tier}</td>
                                    <td className="p-3 text-xs">{f.status}</td>
                                    <td className="p-3 text-right">
                                        <Switch checked={!!f.flag_enabled} disabled={busy[f.feature_code]} onCheckedChange={(v) => toggleFlag(f.feature_code, v)} data-testid={`feature-flag-${f.feature_code}`} />
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </AdminShell>
    );
}

/* ===== Templates ===== */
export function SuperAdminTemplates() {
    const [tpls, setTpls] = useState([]);
    const [busy, setBusy] = useState({});
    const [family, setFamily] = useState("all");
    const [q, setQ] = useState("");

    useEffect(() => {
        api.get("/super-admin/templates")
            .then((r) => setTpls(r.data))
            .catch((e) => toast.error(errMsg(e, "Failed to load templates")));
    }, []);

    // Family filter values come from the DB (Classic, Modern, Editorial,
    // Bold, Elegant, Flagship, etc.). Derive them from the actual data so
    // this stays in sync as new families are added by product.
    const families = ["all", ...Array.from(new Set(tpls.map((t) => t.family).filter(Boolean))).sort()];

    async function toggleActive(code, next) {
        setBusy((b) => ({ ...b, [code]: true }));
        try {
            const r = await api.put(`/super-admin/templates/${code}`, { is_active: next });
            setTpls((xs) => xs.map((t) => (t.code === code ? { ...t, is_active: r.data.is_active, active: r.data.is_active } : t)));
            toast.success(`${r.data.name} ${next ? "activated" : "deactivated"}`);
        } catch (e) {
            toast.error(errMsg(e, "Toggle failed"));
        } finally {
            setBusy((b) => ({ ...b, [code]: false }));
        }
    }

    const filtered = tpls.filter((t) => {
        if (family !== "all" && (t.family || "") !== family) return false;
        if (!q) return true;
        const s = q.toLowerCase();
        return (
            (t.name || "").toLowerCase().includes(s) ||
            (t.code || "").toLowerCase().includes(s) ||
            (t.tagline || "").toLowerCase().includes(s)
        );
    });

    const activeCount = tpls.filter((t) => t.is_active !== false).length;

    return (
        <AdminShell>
            <div className="mb-6 flex flex-col md:flex-row md:items-end md:justify-between gap-3">
                <div>
                    <div className="text-xs uppercase tracking-widest text-[#123B5D]/60">Super admin</div>
                    <h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-templates-header">Templates</h1>
                    <p className="text-[#17212B]/60 text-sm mt-1">
                        {tpls.length} templates registered · {activeCount} active. Deactivating a template hides it from the doctor gallery immediately.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Search name, code, tagline…"
                        className="max-w-xs"
                        data-testid="templates-search"
                    />
                </div>
            </div>

            <div className="mb-4 flex flex-wrap gap-2" data-testid="templates-family-filter">
                {families.map((f) => (
                    <button
                        key={f}
                        onClick={() => setFamily(f)}
                        className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition ${
                            family === f
                                ? "bg-[#123B5D] text-white border-[#123B5D]"
                                : "bg-white text-[#123B5D] border-[#123B5D]/20 hover:border-[#0F766E]"
                        }`}
                        data-testid={`templates-family-${f}`}
                    >
                        {f === "all" ? `All (${tpls.length})` : `${f} (${tpls.filter((t) => t.family === f).length})`}
                    </button>
                ))}
            </div>

            <Card className="border-[#123B5D]/10">
                <CardContent className="p-0">
                    <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                            <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                                <tr>
                                    <th className="text-left p-3">Template</th>
                                    <th className="text-left p-3">Code</th>
                                    <th className="text-left p-3">Family</th>
                                    <th className="text-left p-3">Min tier</th>
                                    <th className="text-left p-3">Tagline</th>
                                    <th className="text-right p-3">Active</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={6} className="p-6 text-center text-[#17212B]/60" data-testid="templates-empty">
                                            No templates match the current filter.
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map((t) => (
                                        <tr key={t.code} className="border-b border-[#123B5D]/5" data-testid={`template-row-${t.code}`}>
                                            <td className="p-3 font-semibold text-[#123B5D]">{t.name}</td>
                                            <td className="p-3 font-mono text-xs text-[#17212B]/70">{t.code}</td>
                                            <td className="p-3 text-xs">{t.family || "—"}</td>
                                            <td className="p-3 text-xs">Tier {t.min_tier ?? "—"}</td>
                                            <td className="p-3 text-xs text-[#17212B]/70 max-w-md truncate">{t.tagline || t.description || "—"}</td>
                                            <td className="p-3 text-right">
                                                <Switch
                                                    checked={t.is_active !== false}
                                                    disabled={busy[t.code]}
                                                    onCheckedChange={(v) => toggleActive(t.code, v)}
                                                    data-testid={`template-active-${t.code}`}
                                                />
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </CardContent>
            </Card>
        </AdminShell>
    );
}

/* ===== Admin users ===== */
export function SuperAdminAdmins() {
    const [admins, setAdmins] = useState([]);
    const [creating, setCreating] = useState(false);
    const [form, setForm] = useState({ email: "", full_name: "", role: "admin" });
    const [tempResult, setTempResult] = useState(null);

    async function load() {
        const r = await api.get("/super-admin/admins");
        setAdmins(r.data);
    }
    useEffect(() => { load(); }, []);

    async function create() {
        setCreating(true);
        try {
            const r = await api.post("/super-admin/admins", form);
            setTempResult(r.data);
            setForm({ email: "", full_name: "", role: "admin" });
            load();
        } catch (e) {
            toast.error(errMsg(e, "Failed"));
        } finally {
            setCreating(false);
        }
    }
    async function toggle(id, disabled) {
        try {
            await api.post(`/super-admin/admins/${id}/${disabled ? "enable" : "disable"}`);
            load();
        } catch (e) { toast.error(errMsg(e, "Failed")); }
    }

    return (
        <AdminShell>
            <div className="mb-6"><h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-admins-header">Admin users</h1></div>
            <Card className="border-[#123B5D]/10 mb-6">
                <CardHeader><CardTitle className="text-sm">Create admin</CardTitle></CardHeader>
                <CardContent className="grid grid-cols-1 md:grid-cols-4 gap-3 items-end">
                    <div><div className="text-xs text-[#17212B]/60 mb-1">Email</div><Input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="admin@example.com" data-testid="new-admin-email" /></div>
                    <div><div className="text-xs text-[#17212B]/60 mb-1">Full name</div><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} placeholder="Full Name" data-testid="new-admin-name" /></div>
                    <div><div className="text-xs text-[#17212B]/60 mb-1">Role</div>
                        <select className="w-full border border-[#123B5D]/20 rounded-md h-10 px-3 text-sm" value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} data-testid="new-admin-role">
                            <option value="admin">admin</option><option value="super_admin">super_admin</option>
                        </select>
                    </div>
                    <Button onClick={create} disabled={creating || !form.email || !form.full_name} data-testid="new-admin-create">Create</Button>
                </CardContent>
            </Card>
            {tempResult && (
                <Card className="border-yellow-200 bg-yellow-50 mb-6" data-testid="new-admin-result">
                    <CardContent className="pt-6">
                        <div className="text-sm font-bold mb-2">Temp password (shown ONCE):</div>
                        <div className="flex items-center gap-2">
                            <code className="bg-white border rounded px-3 py-1 font-mono" data-testid="new-admin-temp-password">{tempResult.temp_password}</code>
                            <Button size="sm" variant="ghost" onClick={() => { navigator.clipboard.writeText(tempResult.temp_password); toast.success("Copied"); }}><Copy size={14} /></Button>
                            <button className="text-xs text-[#17212B]/60 ml-auto" onClick={() => setTempResult(null)}>Dismiss</button>
                        </div>
                        <div className="text-xs text-[#17212B]/70 mt-2">Share this out-of-band. Ask the user to change it after first login.</div>
                    </CardContent>
                </Card>
            )}
            <Card className="border-[#123B5D]/10">
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                            <tr><th className="text-left p-3">Name</th><th className="text-left p-3">Email</th><th className="text-left p-3">Role</th><th className="text-left p-3">Last login</th><th className="text-right p-3">Actions</th></tr>
                        </thead>
                        <tbody>
                            {admins.map((a) => (
                                <tr key={a.id} className="border-b border-[#123B5D]/5" data-testid={`admin-row-${a.id}`}>
                                    <td className="p-3">{a.full_name}{a.is_disabled ? <span className="ml-2 text-xs text-[#DC2626]">disabled</span> : null}</td>
                                    <td className="p-3 text-xs">{a.email}</td>
                                    <td className="p-3 text-xs capitalize">{a.role?.replaceAll("_", " ")}</td>
                                    <td className="p-3 text-xs">{a.last_login_at?.slice(0, 10) || "—"}</td>
                                    <td className="p-3 text-right">
                                        <Button size="sm" variant="outline" onClick={() => toggle(a.id, a.is_disabled)} data-testid={`admin-toggle-${a.id}`}>
                                            {a.is_disabled ? "Enable" : "Disable"}
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </AdminShell>
    );
}

/* ===== Audit logs ===== */
export function SuperAdminAudit() {
    const [rows, setRows] = useState([]);
    const [action, setAction] = useState("");
    const [entity, setEntity] = useState("");
    async function load() {
        const params = new URLSearchParams();
        if (action) params.set("action", action);
        if (entity) params.set("entity", entity);
        const r = await api.get("/super-admin/audit-logs?" + params.toString());
        setRows(r.data.items);
    }
    useEffect(() => { load(); }, [action, entity]);
    return (
        <AdminShell>
            <div className="mb-6"><h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-audit-header">Audit logs</h1></div>
            <div className="flex gap-3 mb-4">
                <Input value={action} onChange={(e) => setAction(e.target.value)} placeholder="Filter by action" className="max-w-xs" data-testid="audit-filter-action" />
                <Input value={entity} onChange={(e) => setEntity(e.target.value)} placeholder="Filter by entity" className="max-w-xs" data-testid="audit-filter-entity" />
            </div>
            <Card className="border-[#123B5D]/10">
                <CardContent className="p-0">
                    <table className="w-full text-sm">
                        <thead className="bg-[#F5F8FA] border-b border-[#123B5D]/10">
                            <tr><th className="text-left p-3">When</th><th className="text-left p-3">Actor</th><th className="text-left p-3">Action</th><th className="text-left p-3">Entity</th><th className="text-left p-3">Old → New</th></tr>
                        </thead>
                        <tbody>
                            {rows.map((r) => (
                                <tr key={r.id} className="border-b border-[#123B5D]/5" data-testid={`audit-row-${r.id}`}>
                                    <td className="p-3 text-xs">{r.created_at?.slice(0, 19)}</td>
                                    <td className="p-3 text-xs">{r.actor_email || r.actor_id?.slice(0, 8)}</td>
                                    <td className="p-3 text-xs font-mono">{r.action}</td>
                                    <td className="p-3 text-xs">{r.entity} · {r.entity_id?.slice(0, 12)}</td>
                                    <td className="p-3 text-xs">{r.old_value ? JSON.stringify(r.old_value).slice(0, 80) : ""} → {r.new_value ? JSON.stringify(r.new_value).slice(0, 80) : ""}</td>
                                </tr>
                            ))}
                            {rows.length === 0 && <tr><td colSpan={5} className="text-center p-6 text-[#17212B]/60">No entries match those filters.</td></tr>}
                        </tbody>
                    </table>
                </CardContent>
            </Card>
        </AdminShell>
    );
}

/* ===== System settings ===== */
export function SuperAdminSettings() {
    const [s, setS] = useState(null);
    const [probing, setProbing] = useState(null);   // null | "auto" | "sandbox" | "production"
    const [probe, setProbe] = useState({});         // {auto: {...}, sandbox: {...}, production: {...}}
    useEffect(() => { api.get("/super-admin/system-settings").then((r) => setS(r.data)); }, []);
    async function verifyCashfree(target = "auto") {
        setProbing(target); setProbe((p) => ({ ...p, [target]: null }));
        try {
            const url = target === "auto"
                ? "/super-admin/gateway-health/probe-cashfree"
                : `/super-admin/gateway-health/probe-cashfree?target=${target}`;
            const r = await api.post(url);
            setProbe((p) => ({ ...p, [target]: r.data }));
        } catch (e) {
            setProbe((p) => ({ ...p, [target]: { last_probe_ok: false,
                last_error: e?.response?.data?.detail || e.message } }));
        } finally { setProbing(null); }
    }
    if (!s) return <AdminShell><div className="text-[#17212B]/60">Loading…</div></AdminShell>;
    const pg = s.payment_gateway || {};
    return (
        <AdminShell>
            <div className="mb-6"><h1 className="text-2xl font-bold text-[#123B5D]" data-testid="super-settings-header">System settings</h1><p className="text-[#17212B]/60 text-sm">Read-only view of platform configuration.</p></div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-[#123B5D]/10 md:col-span-2" data-testid="settings-payment">
                    <CardHeader><CardTitle className="text-sm">Payment gateway</CardTitle></CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-xs text-[#17212B]/60">Active gateway</div>
                                <div className="font-mono font-semibold text-[#123B5D]" data-testid="pg-active">{pg.active}</div>
                            </div>
                            <div>
                                <div className="text-xs text-[#17212B]/60">Cashfree environment</div>
                                <div className="font-mono font-semibold" data-testid="pg-cf-env">{pg.cashfree_env || "—"}</div>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <div className="text-xs text-[#17212B]/60">Production key</div>
                                <div className="font-mono text-xs" data-testid="pg-cf-prod-hint">{pg.cashfree_prod_key_present ? pg.cashfree_prod_key_hint : "not set"}</div>
                            </div>
                            <div>
                                <div className="text-xs text-[#17212B]/60">Sandbox key</div>
                                <div className="font-mono text-xs" data-testid="pg-cf-sbx-hint">{pg.cashfree_sandbox_key_present ? pg.cashfree_sandbox_key_hint : "not set"}</div>
                            </div>
                        </div>
                        <div>
                            <div className="text-xs text-[#17212B]/60">Webhook URL (paste into Cashfree Dashboard → Developers → Webhooks)</div>
                            <code className="block text-xs bg-[#F5F8FA] rounded px-2 py-1 mt-1 break-all" data-testid="pg-webhook-url">{pg.webhook_url}</code>
                        </div>
                        <div className="flex flex-wrap items-center gap-3 pt-1">
                            <button
                                type="button"
                                onClick={() => verifyCashfree("production")}
                                disabled={!!probing || !pg.cashfree_prod_key_present}
                                data-testid="pg-verify-prod-btn"
                                className="rounded-md bg-[#123B5D] text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50">
                                {probing === "production" ? "Verifying…" : "Verify Production"}
                            </button>
                            {probe.production && (
                                <span className="text-xs" data-testid="pg-verify-prod-status">
                                    {probe.production.last_probe_ok
                                        ? <span className="text-[#15803D] font-semibold">Prod · Connected</span>
                                        : <span className="text-[#DC2626] font-semibold">Prod error: {(probe.production.last_error || "unknown").slice(0, 140)}</span>}
                                </span>
                            )}
                            <button
                                type="button"
                                onClick={() => verifyCashfree("sandbox")}
                                disabled={!!probing || !pg.cashfree_sandbox_key_present}
                                data-testid="pg-verify-sandbox-btn"
                                className="rounded-md bg-[#0F766E] text-white text-xs font-semibold px-3 py-1.5 disabled:opacity-50">
                                {probing === "sandbox" ? "Verifying…" : "Verify Sandbox"}
                            </button>
                            {probe.sandbox && (
                                <span className="text-xs" data-testid="pg-verify-sandbox-status">
                                    {probe.sandbox.last_probe_ok
                                        ? <span className="text-[#15803D] font-semibold">Sandbox · Connected</span>
                                        : <span className="text-[#DC2626] font-semibold">Sandbox error: {(probe.sandbox.last_error || "unknown").slice(0, 140)}</span>}
                                </span>
                            )}
                        </div>
                        <p className="text-xs text-[#17212B]/60 pt-2">{pg.note}</p>
                    </CardContent>
                </Card>
                <Card className="border-[#123B5D]/10" data-testid="settings-ai">
                    <CardHeader><CardTitle className="text-sm">AI configuration</CardTitle></CardHeader>
                    <CardContent>
                        <div className="text-xs text-[#17212B]/60">Provider</div>
                        <div className="font-mono font-semibold">{s.ai.provider}</div>
                        <div className="text-xs text-[#17212B]/60 mt-3">Model</div>
                        <div className="font-mono font-semibold">{s.ai.model}</div>
                        <div className="text-xs text-[#17212B]/60 mt-3">Prompt version</div>
                        <div className="font-mono font-semibold">{s.ai.prompt_version}</div>
                    </CardContent>
                </Card>
                <Card className="border-[#123B5D]/10" data-testid="settings-urls">
                    <CardHeader><CardTitle className="text-sm">Public base URL</CardTitle></CardHeader>
                    <CardContent><code className="text-xs">{s.public_base_url}</code></CardContent>
                </Card>
            </div>
        </AdminShell>
    );
}
